// node_modules
import {Dialog} from 'primereact/dialog'
import {Dropdown} from 'primereact/dropdown'
import {InputMask} from 'primereact/inputmask'
import {InputText} from 'primereact/inputtext'
import React, { useContext, useEffect, useState } from "react";
import {Controller, useForm} from 'react-hook-form'
import { useDispatch } from "react-redux";

// redux
import { setLoading } from "../../../redux/slices/app.slice";
import {
	useCreateUserMutation,
	useDeleteUserMutation,
	useLazyGetUserListQuery,
	useUpdateUserMutation, useUpdateUserPasswordMutation
} from '../../../redux/services/user'
import {
	useGetOrganizationListQuery,
} from '../../../redux/services/organization'

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

// context
import { ToastContext } from "../../../App";

// styles
import * as styles from "./UserManagement.module.css";

const UserManagement = () => {
	const showToast = useContext(ToastContext);
	const dispatch = useDispatch();
	
	const [selectedOrganization, setSelectedOrganization] = useState(null)
	const [id, setId] = useState(null)
	const [username, setUsername] = useState(null)
	const [mode, setMode] = useState(null)
	const [showModal, setShowModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	
	const { control, setValue, handleSubmit } = useForm({
		defaultValues: {
			username: "",
			subdomain: "",
			email: "",
			password: "",
			phone: "",
			role: ""
		},
	});
	
	const {data: organizations, isSuccess: isGetOrganizationsSuccess, isError: isGetOrganizationError} = useGetOrganizationListQuery();
	
	const [getUsers, {
		data: users,
		isSuccess: isGetUsersSuccess,
		isLoading: isGetUsersLoading,
		isError: isGetUsersError
	}] = useLazyGetUserListQuery();
	const [createUser, { isSuccess: isCreateUserSuccess, isError: isCreateUserError, error: createUserError }] =
		useCreateUserMutation();
	const [updateUser, { isSuccess: isUpdateUserSuccess, isError: isUpdateUserError, error: updateUserError }] =
		useUpdateUserMutation();
	const [updateUserPassword, { isSuccess: isUpdateUserPasswordSuccess, isError: isUpdateUserPasswordError, error: updateUserPasswordError }] =
		useUpdateUserPasswordMutation();
	const [deleteUser, { isSuccess: isDeleteUserSuccess, isError: isDeleteUserError, error: deleteUserError }] =
		useDeleteUserMutation();
	
	const openModal = (mode, rowData) => () => {
		if (mode === "create") {
			setId(null);
			setValue("username", "");
			setValue("email", "");
			setValue("phone", "");
			setValue("password", "");
			setValue("role", "");
		} else if (mode === "edit") {
			setId(rowData.id);
			setValue("username", rowData.username);
			setValue("email", rowData.email);
			setValue("phone", rowData.phone?.slice(2) || "");
			setValue("role", rowData.role);
		} else {
			setId(rowData.id);
			setValue("password", "");
		}
		setShowModal(true);
		setMode(mode);
	};
	
	const openDeleteModal = (rowData) => () => {
		setId(rowData.id)
		setUsername(rowData.username)
		setShowDeleteModal(true)
	}
	
	useEffect(() => {
		if(selectedOrganization) {
			getUsers(selectedOrganization.subdomain)
		}
	}, [selectedOrganization])
	
	useEffect(() => {
		if(isGetOrganizationsSuccess) {
			dispatch(setLoading(false))
		}
	}, [isGetOrganizationsSuccess])
	
	useEffect(() => {
		if(isGetOrganizationError) {
			showToast('error', 'Organization', 'Unable to load organizations')
			dispatch(setLoading(false));
		}
	}, [isGetOrganizationError])
	
	useEffect(() => {
		if(isGetUsersSuccess)
			dispatch(setLoading(false));
	}, [isGetUsersSuccess])
	
	useEffect(() => {
		if(isGetUsersError) {
			showToast('error', 'User', 'Unable to load users')
			dispatch(setLoading(false));
		}
	}, [isGetUsersError])
	
	useEffect(() => {
		if(isCreateUserSuccess) {
			showToast('success', 'User', 'User created!')
			setShowModal(false)
		}
	}, [isCreateUserSuccess])
	
	useEffect(() => {
		if(isCreateUserError) {
			showToast("error", createUserError?.status, createUserError?.data?.message || (createUserError?.data?.errors && "Incorrect data"))
		}
	}, [isCreateUserError])
	
	useEffect(() => {
		if(isUpdateUserSuccess) {
			showToast('success', 'User', 'User updated!')
			setShowModal(false)
		}
	}, [isUpdateUserSuccess])
	
	useEffect(() => {
		if(isUpdateUserError) {
			showToast("error", updateUserError?.status, updateUserError?.data?.message || (updateUserError?.data?.errors && "Incorrect data"))
		}
	}, [isUpdateUserError])
	
	useEffect(() => {
		if(isUpdateUserPasswordSuccess) {
			showToast('success', 'User', 'User password updated!')
			setShowModal(false)
		}
	}, [isUpdateUserPasswordSuccess])
	
	useEffect(() => {
		if(isUpdateUserPasswordError) {
			showToast("error", updateUserPasswordError?.status, updateUserPasswordError?.data?.message || (updateUserPasswordError?.data?.errors && "Incorrect data"))
		}
	}, [isUpdateUserPasswordError])
	
	useEffect(() => {
		if(isDeleteUserSuccess) {
			showToast('success', 'User', 'User deleted!')
			setShowDeleteModal(false)
		}
	}, [isDeleteUserSuccess])
	
	useEffect(() => {
		if(isDeleteUserError) {
			showToast("error", deleteUserError?.status, deleteUserError?.data?.message || (deleteUserError?.data?.errors && "Incorrect data"))
		}
	}, [isDeleteUserError])
	
	const onCreate = async (data) => {
		const { username, email, password, phone, role } = data;
		
		const payload = {
			username,
			email,
			password,
			phone: `+1${phone}`,
			userRole: role
		};
		
		createUser({payload, subdomain: selectedOrganization.subdomain});
	};
	
	const onSaveEdit = async (data) => {
		const { username, phone, email, role } = data;
		
		const payload = {
			username,
			phone: `+1${phone}`,
			email,
			userRole: role
		};
		
		updateUser({
			id: id,
			payload,
			subdomain: selectedOrganization.subdomain
		});
	};
	
	const onSaveEditPassword = async (data) => {
		const { password } = data;
		
		const payload = {
			password,
			id
		};
		
		updateUserPassword({
			payload,
			subdomain: selectedOrganization.subdomain
		});
	};
	
	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			if (mode === "create") {
				handleSubmit(onCreate)();
			} else if (mode === "edit") {
				handleSubmit(onSaveEdit)();
			} else {
				handleSubmit(onSaveEditPassword)()
			}
		}
	};
	
	const onDelete = () => {
		deleteUser({id, subdomain: selectedOrganization.subdomain});
	}
	
	const actionBodyTemplate = (rowData) => {
		return (
			<div className={styles.cellSoundWrapper}>
				<Button
					icon="pi pi-pencil"
					className={`p-button-text ${styles.soundBtn}`}
					onClick={openModal("edit", rowData)}
				/>
				<Button
					icon="pi pi-key"
					className={`p-button-text ${styles.soundBtn}`}
					onClick={openModal("password", rowData)}
				/>
				<Button
					icon="pi pi-trash"
					className={`p-button-text ${styles.deleteBtn}`}
					onClick={openDeleteModal(rowData)}
				/>
			</div>
		);
	};
	
	return (
		<div className={styles.pageWrapper}>
			{isGetOrganizationsSuccess && (
				<>
					<Dropdown
						value={selectedOrganization}
						onChange={(e) => setSelectedOrganization(e.value)}
						options={organizations?.toSorted((a, b) =>
							a.name.localeCompare(b.name)
						)}
						optionLabel="name"
						placeholder="Select organization"
						className={`${styles.dropdown}`}
					/>
				</>
			)}
			<div
				className={`flex align-items-center justify-content-between ${styles.header}`}
			>
				<div className="flex">
					<h2>Users</h2>
					{selectedOrganization && (
						<span className={styles.organizationLabel}>{selectedOrganization.name}</span>
					)}
				</div>
				{selectedOrganization && (
					<Button
						className={styles.createBtn}
						onClick={openModal("create")}
					>
						ADD USER <i className="pi pi-plus" />
					</Button>
				)}
			</div>
			{selectedOrganization ? (
				<>
					{users && (
						<DataTable value={users} rows={6} loading={isGetUsersLoading}>
							<Column
								field="username"
								header="Username"
								body={(rowData) => {
									return <div className={styles.cellInsider} style={{width: "290px"}}>{rowData.username}</div>;
								}}
							/>
							
							<Column
								field="role"
								header="Role"
								body={(rowData) => {
									return <div className={styles.cellInsider} style={{width: "162px"}}>{rowData.role.slice(0,1).toUpperCase()+rowData.role.slice(1)}</div>;
								}}
							/>
							
							<Column
								field="email"
								header={()=>{
									return <div style={{width: "280px", textAlign: "start" }}>
										Email
									</div>
								}}
								body={(rowData) => {
									return <div className={styles.cellInsider} style={{width: "100%", textAlign: "left"}}>{rowData.email}</div>;
								}}
							/>
							
							<Column
								field="phone"
								header={()=>{
									return <div style={{width: "140px", textAlign: "start" }}>
										Phone
									</div>
								}}
								body={(rowData) => {
									return <div className={styles.cellInsider} style={{width: "100%", textAlign: "left"}}>{rowData.phone}</div>;
								}}
							/>
							
							<Column
								body={actionBodyTemplate}
								header="Actions"
								style={{ width: "200px" }}
							/>
						</DataTable>
					)}
				</>
			) : (
				<div>Please select organization</div>
			)}
			
			<Dialog
				header={`${mode === "create" ? "New User" : mode === "edit" ? "Edit User" : "Edit User Password"}`}
				visible={showModal}
				onHide={() => {
					setShowModal(false);
				}}
				footer={() => (
					<div className="flex gap-2 justify-content-end">
						<Button
							label="Save"
							type="submit"
							className={`p-button-success ${styles.saveBtn}`}
							onClick={
								mode === "create" ? handleSubmit(onCreate) : mode === "edit" ? handleSubmit(onSaveEdit) : handleSubmit(onSaveEditPassword)
							}
						/>
					</div>
				)}
				className={`p-fluid ${styles.userModal}`}
			>
				<form>
					{mode !== "password" && (
						<>
							<Controller
								name="username"
								control={control}
								render={({ field }) => (
									<div className="field">
										<label>Username</label>
										<InputText
											className={`w-full mb-3 ${styles.input}`}
											onKeyDown={handleKeyDown}
											placeholder="Username"
											{...field}
										/>
									</div>
								)}
							/>
							
							<Controller
								name="role"
								control={control}
								render={({ field }) => (
									<div className={`field ${styles.dropDownWrapper}`}>
										<label>Role</label>
										<Dropdown
											id={field.name}
											value={field.value}
											onChange={(e) => field.onChange(e.value)}
											placeholder="Select role"
											className="w-full"
											panelClassName={styles.dropDownPanel}
											options={[
												{ label: "Admin", value: "admin" },
												{ label: "Manager", value: "manager" },
												{ label: "Chef", value: "chef" }
											]}
										/>
									</div>
								)}
							/>
							
							<Controller
								name="email"
								control={control}
								render={({ field }) => (
									<div className="field">
										<label>Email</label>
										<InputText
											className={`w-full mb-3 ${styles.input}`}
											onKeyDown={handleKeyDown}
											placeholder="Email"
											{...field}
										/>
									</div>
								)}
							/>
							
							<Controller
								name="phone"
								control={control}
								rules={{
									required: "Enter your phone number",
									minLength: {
										value: 10,
										message: "Phone number is invalid",
									},
								}}
								render={({ field, fieldState }) => (
									<div className="field">
										<label>Phone Number</label>
										<div>
											<div className="p-inputgroup flex-1">
                  <span className={`p-inputgroup-addon mb-3 ${styles.phonePrefix}`}>
                    +1
                  </span>
												<InputMask
													className={`w-full mb-3 ${styles.input} ${styles.inputPhone} ${
														fieldState.invalid ? "p-invalid" : ""
													}`}
													placeholder="(999) 999-9999"
													prefix="+1"
													mask="(999) 999-9999"
													unmask={true}
													{...field}
												/>
											</div>
											
											{fieldState.error && (
												<small className={`p-error `}>
													{fieldState.error.message}
												</small>
											)}
										</div>
									</div>
								)}
							/>
						</>
					)}
					
					{mode !== "edit" && (
						<Controller
							name="password"
							control={control}
							render={({ field }) => (
								<div className="field">
									<label>Password</label>
									<InputText
										className={`w-full ${styles.input}`}
										onKeyDown={handleKeyDown}
										autoComplete="new-password"
										type="password"
										placeholder="Password"
										{...field}
									/>
								</div>
							)}
						/>
					)}
				</form>
			</Dialog>
			
			<Dialog
				closable={false}
				header={
					<div style={{ width: '100%', textAlign: 'center' }}>
						Are You Sure?
					</div>}
				visible={showDeleteModal}
				onHide={() => {
					setShowDeleteModal(false);
				}}
				footer={() => (
					<div className="flex flex-column gap-2 justify-content-end">
						<Button
							label="Delete"
							className={`p-button-success ${styles.deleteDialogBtn}`}
							onClick={onDelete}
						/>
						<Button
							label="Cancel"
							text
							className={`p-button-success ${styles.cancelDialogBtn}`}
							onClick={() => {setShowDeleteModal(false)}}
						/>
					</div>
				)}
				className={`p-fluid ${styles.deleteModal}`}
			>
				<div className={styles.text}>
					<span>You want to delete user: <strong>{username}</strong>.</span>
					Confirm this action by pressing ‘Delete’ or cancel it.
				</div>
			</Dialog>
		</div>
	);
};

export default UserManagement;
